import { useSelector } from 'react-redux';
import { CustomizePlanButtonGroup, OnboardingChrome } from '../components';
import { selectClient } from '../model';
import { SlideContent } from '../components/SlideContent';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import DomainAddOutlinedIcon from '@mui/icons-material/DomainAddOutlined'; // nursing home
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined'; // independent living
import AddHomeWorkOutlinedIcon from '@mui/icons-material/AddHomeWorkOutlined'; // assisted living
import { FiChevronsRight } from 'react-icons/fi';
import { PageLink } from '../components/PageLink';
import { OnboardingFundingSources } from '../pages';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

import {
  carePhaseDefs,
  CarePhase,
  careEnvironmentDefs,
  carePhaseDefList,
  formatCurrency,
  formatThousands,
  CareEnvironment,
  formatPercent,
  pickClientCareEnvironmentCosts,
  isHealthcareInflationRateDefault,
} from '@shared';
import { OnboardingCareSettingsPhaseThree } from '.';
import { useState } from 'react';
import CareEnvironmentCostModal from '../components/CareEnvironmentCostModal';
import EditHealthCareInflationModal from '../components/EditHealthCareInflationModal';
import { EditIconButton } from '@client/components/EditIconButton';
import { cn } from '@client/lib';
import { HospitalIcon, HouseIcon, HouseHeartIcon, XIcon, EqualIcon } from 'lucide-react';

export function OnboardingCarePhasesSummary({ clientId }: ClientIdProps) {
  const client = useSelector(selectClient)!;
  const {
    phaseCosts,
    appliedCareEnvironments,
    clientPhaseInflationFactors,
    clientYearsTillPhaseEnd,
    intakeSurvey: { clientLtcZipCode },
    allPhaseCosts: { allPhaseInflatedProfessionalShareCost },
    clientCalculationSettings: { healthCareInflationRate },
  } = client;

  const [openEditCostModal, setOpenEditCostModal] = useState(false);
  const [openEditInflationModal, setOpenEditInflationModal] = useState(false);
  const [selectedCareEnvironment, setSelectedCareEnvironment] = useState<CareEnvironment | null>(
    null,
  );

  const handleEditCostClick = (careEnvironment: CareEnvironment) => {
    setSelectedCareEnvironment(careEnvironment);
    setOpenEditCostModal(true);
  };

  const handleEditInflationClick = () => {
    setOpenEditInflationModal(true);
  };

  const carePhaseData = carePhaseDefList.map(phase => {
    const singlePhaseCosts = phaseCosts[phase.index];
    const phaseLabel = carePhaseDefs[phase.value].label;
    const yearsTillPhaseEnd = clientYearsTillPhaseEnd[phase.value];
    const {
      inHomeEnvironment,
      phaseInflatedProfessionalShareCost,
      phaseCareMonthsNeeded,
      phaseProfessionalCareHoursProvided,
    } = singlePhaseCosts;
    const phaseInflationFactor = clientPhaseInflationFactors[phase.value];
    const careEnvironment = appliedCareEnvironments[phase.value];
    const careEnvironmentLabel = careEnvironmentDefs[careEnvironment].label;
    const isDurationCustom = singlePhaseCosts.isDurationCustom;
    const { rateAmount, hasCustomRate, rateLocaleDescription } = pickClientCareEnvironmentCosts(
      client,
      careEnvironment,
    );
    return {
      id: phase.index,
      phaseLabel,
      phaseInflatedProfessionalShareCost,
      careEnvironment,
      careEnvironmentLabel,
      rateAmount,
      hasCustomRate,
      rateLocaleDescription,
      phaseCareMonthsNeeded,
      phaseProfessionalCareHoursProvided,
      phaseInflationFactor,
      inHomeEnvironment,
      yearsTillPhaseEnd,
      isDurationCustom,
    };
  });

  const multSign = <XIcon className="size-4 shrink-0 text-gray-700" />;
  const equalSign = <EqualIcon className="size-4 shrink-0 text-gray-700" />;

  const iconPicker = (careEnvironment: CareEnvironment) => {
    switch (careEnvironment) {
      case CareEnvironment.home:
        return <HouseIcon className="size-4" />;
      case CareEnvironment.fullCareFacility:
        return <HospitalIcon className="size-4" />;
      case CareEnvironment.assistedLiving:
        return <HouseHeartIcon className="size-4" />;
      case CareEnvironment.independentLiving:
        return <HouseIcon className="size-4" />;
    }
  };

  return (
    <OnboardingChrome
      slidePage={OnboardingCarePhasesSummary}
      nextButtonLabel={`Begin Funding Plan`}
      previousSlidePage={
        appliedCareEnvironments[CarePhase.fullCare] === CareEnvironment.home
          ? undefined // default to previous slide
          : OnboardingCareSettingsPhaseThree // skip to care settings
      }
    >
      <SlideContent headerText={<span data-testid="care-summary-header">Your LTC Needs</span>}>
        <CustomizePlanButtonGroup
          client={client}
          className="my-5 lg:mb-10"
        />
        <Accordion
          type="single"
          collapsible
          className="md:mt-20"
        >
          {carePhaseData.map((phase, idx) => {
            if (phase.yearsTillPhaseEnd) {
              return (
                <AccordionItem
                  key={phase.id}
                  value={String(phase.id)}
                  className={cn('border-x border-b-0 border-t', {
                    'rounded-t-xl': idx === 0,
                    'border-t': idx === 1,
                  })}
                >
                  <AccordionTrigger
                    data-testid={`care-summary-phase-${phase.phaseLabel.replace(/\s+/g, '-').toLowerCase()}-button`}
                    className="border-0 p-4"
                  >
                    <div className="flex w-full items-center justify-between pr-4 text-base font-medium md:text-xl">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                        <div className="text-start text-gray-950">{phase.phaseLabel}</div>
                        <div className="flex items-center gap-1.5 rounded-full bg-gray-100 py-1 pl-2 pr-2.5 text-gray-950">
                          {iconPicker(phase.careEnvironment)}
                          <span className="text-sm">{phase.careEnvironmentLabel}</span>
                        </div>
                      </div>
                      <div className="text-gray-950">
                        {formatCurrency(phase.phaseInflatedProfessionalShareCost)}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-0">
                    <div className="flex flex-col items-center gap-2 border-t border-gray-200 p-4 sm:flex-row">
                      <div className="flex w-full flex-row-reverse items-center justify-between rounded-lg bg-gray-100 px-2 py-1 sm:flex-col sm:items-start">
                        <div className="flex items-center gap-px">
                          <p className="text-base text-gray-950">
                            {`${formatCurrency(phase.rateAmount)}/${phase.inHomeEnvironment ? 'hr' : 'mo'}`}
                          </p>
                          <EditIconButton
                            onClick={() => handleEditCostClick(phase.careEnvironment)}
                            tooltipText="Edit cost"
                          />
                        </div>
                        <p className="pl-1 text-sm font-medium text-gray-700 sm:pl-0">
                          {phase.rateLocaleDescription}
                        </p>
                      </div>
                      {multSign}
                      <div className="flex w-full flex-row-reverse items-center justify-between rounded-lg bg-gray-100 px-2 py-2 sm:flex-col sm:items-start sm:py-1">
                        <p className="pr-1 text-base text-gray-950 sm:pl-0">
                          {phase.inHomeEnvironment
                            ? `${formatThousands(phase.phaseProfessionalCareHoursProvided)} hours`
                            : `${formatThousands(phase.phaseCareMonthsNeeded)} months`}
                        </p>
                        <p className="pl-1 text-sm font-medium text-gray-700 sm:pl-0">
                          {phase.inHomeEnvironment
                            ? `Professional care hours`
                            : `${phase.isDurationCustom ? 'Custom duration' : 'Estimated duration'}`}
                        </p>
                      </div>
                      {multSign}
                      <div className="flex w-full flex-row-reverse items-center justify-between rounded-lg bg-gray-100 px-2 py-1 sm:flex-col sm:items-start">
                        <div className="flex items-center gap-px">
                          <p className="text-base text-gray-950">
                            {formatPercent(phase.phaseInflationFactor, 1)}
                          </p>
                          <EditIconButton
                            onClick={handleEditInflationClick}
                            tooltipText="Edit inflation rate"
                          />
                        </div>
                        <p className="pl-1 text-sm font-medium text-gray-700 sm:pl-0">
                          Cost growth over{' '}
                          {phase.yearsTillPhaseEnd ? formatThousands(phase.yearsTillPhaseEnd) : '0'}{' '}
                          years
                          {isHealthcareInflationRateDefault(healthCareInflationRate)
                            ? ''
                            : ' (custom rate)'}
                        </p>
                      </div>
                      {equalSign}
                      <div className="flex w-full flex-row-reverse items-center justify-between rounded-lg bg-gray-100 px-2 py-2 sm:w-max sm:flex-col sm:items-start sm:py-1">
                        <p className="pr-1 text-base text-gray-950 sm:pl-0">
                          {formatCurrency(phase.phaseInflatedProfessionalShareCost)}
                        </p>
                        <p className="pl-1 text-sm font-medium text-gray-700 sm:pl-0">Total</p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            } else {
              return null;
            }
          })}
        </Accordion>
        <div className="flex justify-between rounded-b-xl border border-gray-200 bg-gray-100 py-4 pl-4 pr-14">
          <div className="text-base text-gray-950 md:text-2xl">Total Care Needs</div>
          <div
            className="text-base text-gray-950 md:text-2xl"
            data-testid="care-summary-total-amount"
          >
            {formatCurrency(allPhaseInflatedProfessionalShareCost)}
          </div>
        </div>
        <PageLink
          className="mt-6 hidden gap-2 text-lg font-light hover:underline md:flex md:text-xl"
          to={OnboardingFundingSources}
          targetProps={{ clientId }}
        >
          <div className="flex items-center gap-1 font-normal">
            <FiChevronsRight className="h-6 w-6" />
            Next step:
          </div>
          <span> Explore the best ways to protect yourself in old age</span>
        </PageLink>
        {selectedCareEnvironment && (
          <CareEnvironmentCostModal
            selectedCareEnvironment={selectedCareEnvironment}
            open={openEditCostModal}
            setOpen={setOpenEditCostModal}
          />
        )}
        <EditHealthCareInflationModal
          open={openEditInflationModal}
          setOpen={setOpenEditInflationModal}
        />
      </SlideContent>
    </OnboardingChrome>
  );
}
